import React, {useState, useEffect} from "react";
import {WorkExperience} from "../types";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {Log} from "../utils/Log";
import {WorkExperienceAPI} from "../services/API/WorkExperienceAPI";
import {useTranslation} from "react-i18next";
import {Translate as TranslateIcon} from "@mui/icons-material";
import {WorkExperienceTranDTO} from "../types/DTOs/Translation/WorkExperienceTranDTO";
import {Translate} from "../utils/Translate";
import {TranslationAPI} from "../services/API/TranslationAPI";
import {SectionTypes} from "../utils/enums";

// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANG = 'original';

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────
interface WorkExperienceProps {
    item: WorkExperience | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
    /** Lingue già tradotte per questo elemento */
    translatedLanguages?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const fetchAvailableLanguages = async (): Promise<{ code: string; i18nKey: string }[]> => {
    return Translate.LanguageForCombo();
};

const fetchWorkExperienceTranslation = async (
    workExperienceId: string,
    languageCode: string
): Promise<WorkExperienceTranDTO | null | undefined> => {
    const dataTran = await TranslationAPI.Instance<TranslationAPI>().getTranslatedItem(workExperienceId, SectionTypes.EXPERIENCE, languageCode);
    if (dataTran === null || dataTran === undefined) return null;
    return dataTran as WorkExperienceTranDTO;
};

const saveWorkExperienceTranslation = async (data: WorkExperienceTranDTO): Promise<void> => {
    await TranslationAPI.Instance<TranslationAPI>().saveTranslateItem(data, SectionTypes.EXPERIENCE);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
export const EditWorkExperience: React.FC<WorkExperienceProps> = ({
                                                                      item,
                                                                      onSave,
                                                                      onCancel,
                                                                      open,
                                                                      onClose,
                                                                      translatedLanguages = [],
                                                                  }) => {
    // ── Stato originale ──
    const [experience, setExperience] = useState<WorkExperience>({
        id: '', userId: '', company: '', role: '',
        startDate: new Date(), description: '', createdAt: '', updatedAt: ''
    });

    // ── Stato traduzione ──
    const [availableLanguages, setAvailableLanguages] = useState<{ code: string; i18nKey: string }[]>([]);
    const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANG);
    const [translation, setTranslation] = useState<WorkExperienceTranDTO>({
        id: '',
        workExperienceId: '',
        role: '',
        description: '',
        achievements: '',
    });
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState<string>('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const isTranslationMode = selectedLang !== DEFAULT_LANG;

    const { t } = useTranslation();

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setSelectedLang(DEFAULT_LANG);
            setTranslationError('');
            setSaveSuccess(false);

            if (item === null) {
                setExperienceDefault();
            } else {
                setExperience(item);
            }

            fetchAvailableLanguages()
                .then(setAvailableLanguages)
                .catch(() => {});
        }
    }, [item, open]);

    useEffect(() => {
        if (!isTranslationMode || !experience.id) return;

        setTranslationLoading(true);
        setTranslationError('');
        setSaveSuccess(false);

        fetchWorkExperienceTranslation(experience.id, selectedLang)
            .then(data => {
                if (data) {
                    setTranslation(data);
                } else {
                    setTranslation({
                        id: '',
                        workExperienceId: experience.id,
                        role: '',
                        description: '',
                        achievements: '',
                    });
                }
            })
            .catch(() => setTranslationError(t('translation.errorGeneric')))
            .finally(() => setTranslationLoading(false));
    }, [selectedLang]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const setExperienceDefault = () => {
        setExperience({
            id: '', userId: '', company: '', role: '',
            startDate: new Date(), description: '', createdAt: '', updatedAt: ''
        });
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setExperience(prev => ({ ...prev, [name]: value }));
    };

    const handleTranslationInputChange = (event: any) => {
        const { name, value } = event.target;
        setTranslation(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (name: string, value: any) => {
        setExperience(prev => ({ ...prev, [name]: value }));
    };

    const handleLangChange = (e: SelectChangeEvent) => {
        setSelectedLang(e.target.value);
        setSaveSuccess(false);
    };

    // Salva originale
    const handleSaveItem = async () => {
        try {
            if (!experience.id) {
                await WorkExperienceAPI.Instance<WorkExperienceAPI>().createWorkExperience({
                    role: experience.role,
                    startDate: experience.startDate.toISOString().split('T')[0],
                    endDate: experience.endDate ? experience.endDate.toISOString().split('T')[0] : null,
                    description: experience.description,
                    achievements: experience.achievements,
                    company: experience.company
                });
            } else {
                try { Log.writeLogDebug(experience.startDate); } catch (e) { Log.writeLogDebug(e); }

                await WorkExperienceAPI.Instance<WorkExperienceAPI>().updateWorkExperience(experience.id, {
                    role: experience.role,
                    startDate: experience.startDate.toString().split('T')[0],
                    endDate: experience.endDate ? experience.endDate.toISOString().split('T')[0] : null,
                    description: experience.description,
                    achievements: experience.achievements,
                    company: experience.company
                });
            }

            setExperienceDefault();
            onSave();
        } catch (error) {
            console.error('Error saving work experience:', error);
        }
    };

    // Salva traduzione (non chiude la dialog)
    const handleSaveTranslation = async () => {
        setSaveLoading(true);
        setSaveSuccess(false);
        setTranslationError('');
        try {
            await saveWorkExperienceTranslation({
                ...translation,
                workExperienceId: experience.id,
            });
            setSaveSuccess(true);
        } catch {
            setTranslationError(t('translation.errorGeneric'));
        } finally {
            setSaveLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    const langLabel = availableLanguages.find(l => l.code === selectedLang)?.i18nKey ?? '';

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>

            {/* ── Titolo ── */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t('workExperienceDialog.title')}</span>
                    {isTranslationMode && (
                        <Chip
                            icon={<TranslateIcon />}
                            label={t(langLabel)}
                            color="primary"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            {/* ── Banda lingua ── */}
            {experience.id && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('translation.selectLanguageLabel')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('translation.selectLanguageLabel')}
                            onChange={handleLangChange}
                            startAdornment={<TranslateIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                        >
                            <MenuItem value={DEFAULT_LANG}>
                                <em>{t('translation.originalLanguage')}</em>
                            </MenuItem>
                            <Divider />
                            {availableLanguages.map(lang => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {t(lang.i18nKey)}
                                        {translatedLanguages.includes(lang.code) && (
                                            <Chip
                                                label={t('translation.alreadyTranslatedChip')}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isTranslationMode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {translatedLanguages.includes(selectedLang)
                                ? t('translation.editModeExisting')
                                : t('translation.editModeNew')}
                        </Typography>
                    )}
                </Box>
            )}

            <DialogContent sx={{ pt: 2 }}>

                {translationLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {translationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{translationError}</Alert>
                )}

                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>{t('translation.saveSuccess')}</Alert>
                )}

                {!translationLoading && (
                    <>
                        {/* ── Azienda — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <TextField
                                autoFocus
                                name="company"
                                margin="dense"
                                label={t('workExperienceDialog.company')}
                                fullWidth
                                variant="outlined"
                                value={experience.company}
                                onChange={handleInputChange}
                                disabled={isTranslationMode}
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>

                        {/* ── Ruolo — traducibile ── */}
                        <TextField
                            name="role"
                            margin="dense"
                            label={t('workExperienceDialog.role')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.role : experience.role}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Date — NON traducibili ── */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Tooltip
                                title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                                placement="top-start"
                            >
                                <span style={{ display: 'block', marginBottom: 8 }}>
                                    <DatePicker
                                        name="startDate"
                                        label={t('workExperienceDialog.startDate')}
                                        views={['month', 'year']}
                                        format="MM/YYYY"
                                        value={dayjs(experience.startDate)}
                                        onChange={(date) => handleDateChange('startDate', date)}
                                        disabled={isTranslationMode}
                                        sx={{ width: '100%' }}
                                    />
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                                placement="top-start"
                            >
                                <span style={{ display: 'block', marginBottom: 8 }}>
                                    <DatePicker
                                        name="endDate"
                                        label={t('workExperienceDialog.endDate')}
                                        views={['month', 'year']}
                                        format="MM/YYYY"
                                        value={experience.endDate ? dayjs(experience.endDate) : null}
                                        onChange={(date) => handleDateChange('endDate', date)}
                                        disabled={isTranslationMode}
                                        sx={{ width: '100%' }}
                                    />
                                </span>
                            </Tooltip>
                        </LocalizationProvider>

                        {/* ── Descrizione — traducibile ── */}
                        <TextField
                            name="description"
                            margin="dense"
                            label={t('workExperienceDialog.description')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.description : experience.description}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Attività — traducibile ── */}
                        <TextField
                            name="achievements"
                            margin="dense"
                            label={t('workExperienceDialog.achievement')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.achievements : experience.achievements}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />
                    </>
                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('standardUse.undoBtn')}
                </Button>

                {isTranslationMode ? (
                    <Button
                        onClick={handleSaveTranslation}
                        variant="contained"
                        color="primary"
                        disabled={saveLoading || translationLoading}
                        startIcon={saveLoading
                            ? <CircularProgress size={16} color="inherit" />
                            : <TranslateIcon />
                        }
                    >
                        {saveLoading
                            ? t('translation.savingButton')
                            : t('translation.saveTranslationButton', { lang: t(langLabel) })}
                    </Button>
                ) : (
                    <Button onClick={handleSaveItem} variant="contained">
                        {t('standardUse.saveBtn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};